import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import SaveIcon from '@mui/icons-material/Save'
import RefreshIcon from '@mui/icons-material/Refresh'
import SettingsIcon from '@mui/icons-material/Settings'

// Import our sections
import PlatformSettings from './sections/PlatformSettings'
import RegistrationSettings from './sections/RegistrationSettings'
import SecuritySettings from './sections/SecuritySettings'
import AppearanceSettings from './sections/AppearanceSettings'
import IntegrationSettings from './sections/IntegrationSettings'
import AdvancedSettings from './sections/AdvancedSettings'
import BrandingSettings from './sections/BrandingSettings'

// Import hooks and types
import { useEnvironmentConfig } from './hooks/useEnvironmentConfig'
import { BrandingConfig, EnvironmentConfig as ConfigType } from './types/EnvironmentConfig.types'
import { defaultEnvironmentConfig } from './utils/defaultConfigs'
import { SEVV_TABS_OVERFLOW_SX } from '../../../../../theme/sevvTabsOverflowSx'
import { mergeThemeTokens, type SevvThemeTokens } from '../../../../../theme/sevvThemeTokenModel'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`environment-config-tabpanel-${index}`}
      aria-labelledby={`environment-config-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `environment-config-tab-${index}`,
    'aria-controls': `environment-config-tabpanel-${index}`,
  }
}

/**
 * Main Environment Configuration Component
 * Provides a comprehensive interface for managing platform settings
 */
const EnvironmentConfig: React.FC = () => {
  const theme = useTheme()
  const [activeTab, setActiveTab] = useState(0)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [saveError, setSaveError] = useState('')

  // Use our custom hook for configuration management
  const {
    config,
    loading,
    settingsLoaded,
    noSavedSettings,
    blockedDomainsShown,
    saving,
    errors,
    warnings,
    hasChanges,
    updateConfig,
    saveConfig,
    resetConfig,
    validateConfig,
  } = useEnvironmentConfig()

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleSave = async () => {
    try {
      setSaveError('')
      await saveConfig()
      setSuccessMessage('Configuration saved successfully!')
      setShowSuccessMessage(true)
    } catch (error) {
      console.error('Failed to save configuration:', error)
      // Not the green "Success!" box: a failed save has to read as one, with cloud's reason.
      setShowSuccessMessage(false)
      const reason = error instanceof Error && error.message ? ` ${error.message}` : ''
      setSaveError(`The settings weren't saved.${reason}`)
    }
  }

  const handleReset = () => {
    resetConfig()
    setSaveError('')
    setSuccessMessage('Configuration reset to defaults!')
    setShowSuccessMessage(true)
  }

  const handleSectionUpdate = (sectionKey: keyof ConfigType, updates: Partial<ConfigType[keyof ConfigType]>) => {
    if (sectionKey === 'branding') {
      const u = updates as Partial<BrandingConfig>
      const nextBranding = { ...config.branding, ...u }
      const tokenPatch: Partial<SevvThemeTokens> = {
        palette: {
          ...config.designTokens.palette,
          ...(u.primaryColor != null ? { primary: u.primaryColor } : {}),
          ...(u.secondaryColor != null ? { secondary: u.secondaryColor } : {}),
        },
      }
      const shouldPatchTokens = u.primaryColor != null || u.secondaryColor != null
      updateConfig({
        branding: nextBranding,
        ...(shouldPatchTokens && { designTokens: mergeThemeTokens(config.designTokens, tokenPatch) }),
      })
      return
    }
    if (sectionKey === 'designTokens') {
      const u = updates as Partial<SevvThemeTokens>
      const merged = mergeThemeTokens(config.designTokens, u)
      const brandPatch: Partial<BrandingConfig> = {}
      if (u.palette?.primary) brandPatch.primaryColor = u.palette.primary
      if (u.palette?.secondary) brandPatch.secondaryColor = u.palette.secondary
      updateConfig({
        designTokens: merged,
        ...(Object.keys(brandPatch).length > 0 && { branding: { ...config.branding, ...brandPatch } }),
      })
      return
    }
    updateConfig({
      [sectionKey]: {
        ...config[sectionKey],
        ...updates,
      },
    })
  }

  const tabs = [
    { label: 'Platform', component: 'platform' },
    { label: 'Registration', component: 'registration' },
    { label: 'Security', component: 'security' },
    { label: 'Appearance', component: 'appearance' },
    { label: 'Branding', component: 'branding' },
    { label: 'Integrations', component: 'integrations' },
    { label: 'Advanced', component: 'advanced' },
  ]

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: 2,
          background: 'background.paper',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: theme.shadows[2],
          backgroundImage: 'none',
        }}
      >
        <CircularProgress size={48} sx={{ color: 'primary.main' }} />
        <Typography 
          variant="h6" 
          sx={{ 
            color: 'text.primary',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
            fontWeight: 600
          }}
        >
          Loading configuration...
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          pb: 3,
          borderBottom: '2px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: 'text.primary',
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
              }}
            >
              Environment Configuration
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
              }}
            >
              Manage platform settings, security policies, and integrations
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            disabled={saving}
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
              '&:disabled': {
                borderColor: 'divider',
                color: 'text.disabled',
              },
            }}
          >
            Reset to Defaults
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !hasChanges || !settingsLoaded}
            sx={{
              minWidth: 140,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              color: 'primary.contrastText',
              boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 6px 20px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
              },
              '&:disabled': {
                background: theme.palette.action.disabledBackground,
                color: 'text.disabled',
                boxShadow: 'none',
              },
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {/* Success Message */}
      {showSuccessMessage && (
        <Box
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: alpha(theme.palette.success.main, 0.1),
            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            borderRadius: 2,
          }}
        >
          <Typography 
            variant="subtitle2" 
            sx={{ 
              mb: 1, 
              color: 'success.main',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              fontWeight: 600
            }}
          >
            Success!
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'success.main',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
            }}
          >
            {successMessage}
          </Typography>
        </Box>
      )}

      {/* Load failure: Save stays off so fallback defaults never overwrite the stored settings */}
      {!settingsLoaded && (
        <Box
          role="alert"
          data-testid="environment-config-load-failed"
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            Settings couldn't be loaded, so saving is turned off. Reload the page to try again.
          </Typography>
        </Box>
      )}

      {saveError && (
        <Box
          role="alert"
          data-testid="environment-config-save-failed"
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {saveError}
          </Typography>
        </Box>
      )}

      {noSavedSettings && (
        <Box
          data-testid="environment-config-no-saved-settings"
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: alpha(theme.palette.info.main, 0.1),
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: 'info.main' }}>
            No settings saved yet - saving will create them.
          </Typography>
        </Box>
      )}

      {/* Error Display */}
      {errors.length > 0 && (
        <Box
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            color: 'error.main',
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'error.main' }}>
            Configuration Errors ({errors.length}):
          </Typography>
          {errors.map((error, index) => (
            <Typography key={index} variant="body2" sx={{ color: 'error.main', mb: 0.5 }}>
              • {error.message}
            </Typography>
          ))}
        </Box>
      )}

      {/* Warning Display */}
      {warnings.length > 0 && (
        <Box
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: alpha(theme.palette.warning.main, 0.1),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            color: 'warning.main',
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'warning.main' }}>
            Configuration Warnings ({warnings.length}):
          </Typography>
          {warnings.map((warning, index) => (
            <Typography key={index} variant="body2" sx={{ color: 'warning.main', mb: 0.5 }}>
              • {warning.message}
            </Typography>
          ))}
        </Box>
      )}

      {/* Main Content */}
      <Box
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          boxShadow: theme.shadows[4],
          backgroundImage: 'none',
        }}
      >
        {/* Tabs */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          overflow: 'hidden',
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="environment configuration tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              ...SEVV_TABS_OVERFLOW_SX,
              '& .MuiTab-root': {
                color: 'text.secondary',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                padding: '16px 20px',
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 700,
                },
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab key={tab.label} label={tab.label} {...a11yProps(index)} />
            ))}
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ backgroundColor: 'background.paper', backgroundImage: 'none' }}>
          <TabPanel value={activeTab} index={0}>
            <PlatformSettings
              config={config.platform}
              onChange={(updates) => handleSectionUpdate('platform', updates)}
              disabled={saving}
            />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <RegistrationSettings
              config={config.registration}
              onChange={(updates) => handleSectionUpdate('registration', updates)}
              disabled={saving}
              blockedDomainsShown={blockedDomainsShown}
            />
          </TabPanel>
          <TabPanel value={activeTab} index={2}>
            <SecuritySettings
              config={config.security}
              onChange={(updates) => handleSectionUpdate('security', updates)}
              disabled={saving}
            />
          </TabPanel>
          <TabPanel value={activeTab} index={3}>
            <AppearanceSettings
              config={config.appearance}
              onChange={(updates) => handleSectionUpdate('appearance', updates)}
              designTokens={config.designTokens}
              onDesignTokensChange={(updates) => handleSectionUpdate('designTokens', updates)}
              disabled={saving}
            />
          </TabPanel>
          <TabPanel value={activeTab} index={4}>
            <BrandingSettings
              config={config.branding}
              onChange={(updates) => handleSectionUpdate('branding', updates)}
              appearance={config.appearance}
              designTokens={config.designTokens}
              disabled={saving}
            />
          </TabPanel>
          <TabPanel value={activeTab} index={5}>
            <IntegrationSettings
              config={config.integrations}
              onChange={(updates) => handleSectionUpdate('integrations', updates)}
              disabled={saving}
              platformName={config.branding?.platformName}
            />
          </TabPanel>
          <TabPanel value={activeTab} index={6}>
            <AdvancedSettings
              config={config.advanced}
              onChange={(updates) => handleSectionUpdate('advanced', updates)}
              disabled={saving}
            />
          </TabPanel>
        </Box>
      </Box>
    </Box>
  )
}

export default EnvironmentConfig 